import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  InputBase,
  Drawer,
  Divider,
  TextField,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  Paper,
  Pagination,
  Tooltip,
  IconButton,
  Modal,
  Chip,
  Avatar,
  Stack,
} from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { GrView } from "react-icons/gr";
import { useNavigate } from "react-router";
import moment from "moment";
import Controls from "../../Component/controls/Controls";
import { useForm } from "../../Utils/useForm";
import Notification from "../../Utils/Notification";
import { buyerStatusChange, getUserRequestList } from "../../state/action/userAction";

const initialValues = { userName: "", companyName: "", email: "", mobile: "" };

// ── Styled components ──────────────────────────────────────────────
const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#f8fafc",
    color: "#374151",
    fontSize: 13,
    fontWeight: 600,
    border: 0,
    padding: "14px 20px",
    whiteSpace: "nowrap",
    cursor: "pointer",
    userSelect: "none",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#111827",
    border: 0,
    padding: "13px 20px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: "#fff",
  "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
  "&:hover": { backgroundColor: "#f1f5f9 !important", cursor: "pointer" },
  "&:last-child td": { border: 0 },
}));

const FilterTextField = styled(TextField)({
  "& .MuiOutlinedInput-input": { padding: "10px 14px", color: "#000" },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(107,114,128,0.4)",
    borderRadius: "8px",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#4f46e5" },
  "& .MuiInputLabel-root": { color: "rgba(92,91,91,0.7)", fontSize: "14px" },
  "& label.Mui-focused": { color: "#4f46e5" },
});

const FilterLabel = styled(FormLabel)({
  display: "block",
  fontWeight: 600,
  fontSize: "13px",
  color: "#374151",
  marginTop: "16px",
  marginBottom: "6px",
});

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 440,
  bgcolor: "#fff",
  borderRadius: "14px",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  p: 4,
  outline: 0,
};

const SortIcon = ({ flag }) =>
  flag ? (
    <ArrowUpwardIcon sx={{ fontSize: "13px", ml: 0.5, color: "rgba(107,114,128,0.6)", verticalAlign: "middle" }} />
  ) : (
    <ArrowDownwardIcon sx={{ fontSize: "13px", ml: 0.5, color: "rgba(107,114,128,0.6)", verticalAlign: "middle" }} />
  );

// ── Component ──────────────────────────────────────────────────────
export default function UserRequest() {
  const [userList, setUserList] = useState([]);
  const [pagePerSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterData, setFilterData] = useState(true);
  const [filter, setFilter] = useState(false);
  const [fieldName, setFieldName] = useState("");
  const [flagName, setFlagName] = useState(false);
  const [flagEmail, setFlagEmail] = useState(false);
  const [flagMobile, setFlagMobile] = useState(false);
  const [flagCompany, setFlagCompany] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [switchId, setSwitchId] = useState(null);
  const [notify, setNotify] = useState({});

  const navigate = useNavigate();
  const { values, handleInputChange, resetForm } = useForm(initialValues);

  const fetchList = async (pg, name = "", flag) => {
    const string = Object.entries(values).map(([k, v]) => `&${k}=${v}`).join("");
    const query = `?page=${pg}&pagePerSize=${pagePerSize}&search=${search}${string}&sortBy=${name}&sortFlag=${flag}`;
    const res = await getUserRequestList(query);
    if (res.status === true) {
      setUserList(res.data);
      setTotalRecords(res.totalUser);
      setTotalPages(Math.ceil(res.totalUser / pagePerSize));
      setFilter(false);
    } else {
      setUserList([]);
      setTotalRecords(0);
      setTotalPages(0);
    }
  };

  useEffect(() => { fetchList(page, "", ""); }, [search, filterData]);

  const handleChangePage = (e, val) => {
    setPage(val);
    if (fieldName === "userName") fetchList(val, fieldName, flagName);
    else if (fieldName === "email") fetchList(val, fieldName, flagEmail);
    else if (fieldName === "mobile") fetchList(val, fieldName, flagMobile);
    else if (fieldName === "companyName") fetchList(val, fieldName, flagCompany);
    else fetchList(val, "", "");
  };

  const dataSorting = (name) => {
    setFieldName(name);
    if (name === "userName") { setFlagName(!flagName); fetchList(page, name, !flagName); }
    if (name === "email") { setFlagEmail(!flagEmail); fetchList(page, name, !flagEmail); }
    if (name === "mobile") { setFlagMobile(!flagMobile); fetchList(page, name, !flagMobile); }
    if (name === "companyName") { setFlagCompany(!flagCompany); fetchList(page, name, !flagCompany); }
  };

  const handleReject = async () => {
    const res = await buyerStatusChange(switchId, { status: 4, reason: values?.reason });
    if (res?.status === true) {
      fetchList(page, fieldName, flagName);
      setOpenRejectModal(false);
      setNotify({ isOpen: true, message: res.message || "Request rejected.", type: "success" });
    } else {
      setNotify({ isOpen: true, message: res.message || "Something went wrong.", type: "error" });
    }
  };

  const handleAccept = (userId) => {
    navigate("/userDetail", { state: userId });
  };

  return (
    <>
      {/* ── Page Header ── */}
      <Box sx={{ px: 3, pt: 4, pb: 2, backgroundColor: "rgb(241,245,249)" }}>
        <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "22px" }}>
          User Requests
        </Typography>
        <Typography sx={{ color: "rgb(107,114,128)", fontSize: "13px", mt: 0.5 }}>
          Review and manage pending user registration requests
        </Typography>
      </Box>

      {/* ── Toolbar ── */}
      <Box sx={{ px: 3, pb: 2, backgroundColor: "rgb(241,245,249)" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#fff",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "10px",
              px: 1.5,
              py: 0.5,
              minWidth: 260,
            }}
          >
            <SearchIcon sx={{ color: "rgba(107,114,128,0.6)", fontSize: "20px", mr: 1 }} />
            <InputBase
              placeholder="Search requests..."
              sx={{ fontSize: "14px", color: "#000", flex: 1 }}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<FilterAltIcon />}
            onClick={() => setFilter(true)}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 2.5,
              color: "rgb(30,41,59)",
              borderColor: "rgba(30,41,59,0.3)",
              "&:hover": { backgroundColor: "rgb(30,41,59)", color: "#fff", borderColor: "rgb(30,41,59)" },
            }}
          >
            Filter
          </Button>
        </Box>
      </Box>

      {/* ── Table Card ── */}
      <Box sx={{ px: 3, pb: 4, backgroundColor: "rgb(241,245,249)" }}>
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "14px",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell onClick={() => dataSorting("userName")}>
                    User Name <SortIcon flag={flagName} />
                  </StyledTableCell>
                  <StyledTableCell onClick={() => dataSorting("companyName")}>
                    Company Name <SortIcon flag={flagCompany} />
                  </StyledTableCell>
                  <StyledTableCell onClick={() => dataSorting("email")}>
                    Email <SortIcon flag={flagEmail} />
                  </StyledTableCell>
                  <StyledTableCell onClick={() => dataSorting("mobile")}>
                    Mobile <SortIcon flag={flagMobile} />
                  </StyledTableCell>
                  <StyledTableCell>Requested On</StyledTableCell>
                  <StyledTableCell align="center">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#6b7280", fontSize: "15px", border: 0 }}>
                      No pending requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  userList.map((row, index) => (
                    <StyledTableRow
                      key={index}
                      onClick={() => navigate("/userDetail", { state: row.userId })}
                    >
                      {/* User Name with Avatar */}
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              fontSize: "13px",
                              fontWeight: 700,
                              backgroundColor: "rgba(79,70,229,0.12)",
                              color: "rgb(79,70,229)",
                            }}
                          >
                            {row.userName?.charAt(0)?.toUpperCase() || "U"}
                          </Avatar>
                          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#111827" }}>
                            {row.userName}
                          </Typography>
                        </Box>
                      </StyledTableCell>

                      <StyledTableCell>{row.companyName || "—"}</StyledTableCell>
                      <StyledTableCell sx={{ color: "#6b7280" }}>{row.email}</StyledTableCell>
                      <StyledTableCell>{row.mobile}</StyledTableCell>
                      <StyledTableCell sx={{ color: "#6b7280", fontSize: "13px" }}>
                        {moment(row.createdAt).format("DD MMM YYYY")}
                        <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.2 }}>
                          {moment(row.createdAt).fromNow()}
                        </Typography>
                      </StyledTableCell>

                      {/* Actions */}
                      <StyledTableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => navigate("/userDetail", { state: row.userId })}
                              sx={{ color: "#6b7280", "&:hover": { color: "rgb(79,70,229)", backgroundColor: "rgba(79,70,229,0.08)" } }}
                            >
                              <GrView size={15} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Accept Request">
                            <Button
                              size="small"
                              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "15px !important" }} />}
                              onClick={() => handleAccept(row.userId)}
                              sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "rgb(22,163,74)",
                                backgroundColor: "rgba(34,197,94,0.08)",
                                border: "1px solid rgba(34,197,94,0.25)",
                                borderRadius: "8px",
                                px: 1.5,
                                py: 0.5,
                                "&:hover": { backgroundColor: "rgba(34,197,94,0.15)" },
                              }}
                            >
                              Accept
                            </Button>
                          </Tooltip>
                          <Tooltip title="Reject Request">
                            <Button
                              size="small"
                              startIcon={<CancelOutlinedIcon sx={{ fontSize: "15px !important" }} />}
                              onClick={() => { setOpenRejectModal(true); setSwitchId(row.userId); }}
                              sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "rgb(220,38,38)",
                                backgroundColor: "rgba(239,68,68,0.08)",
                                border: "1px solid rgba(239,68,68,0.25)",
                                borderRadius: "8px",
                                px: 1.5,
                                py: 0.5,
                                "&:hover": { backgroundColor: "rgba(239,68,68,0.15)" },
                              }}
                            >
                              Reject
                            </Button>
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {userList.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.5,
                py: 2,
                borderTop: "1px solid #f3f4f6",
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>
                Showing {Math.min((page - 1) * pagePerSize + 1, totalRecords)}–{Math.min(page * pagePerSize, totalRecords)} of {totalRecords} results
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                shape="rounded"
                size="small"
                sx={{
                  "& .MuiButtonBase-root": { color: "#374151", fontSize: "13px" },
                  "& .Mui-selected": { backgroundColor: "rgb(79,70,229) !important", color: "#fff !important" },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Reject Modal ── */}
      <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
        <Box sx={modalStyle}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}>
                Reject Request
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#6b7280", mt: 0.3 }}>
                Please provide a reason for rejection
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpenRejectModal(false)} sx={{ color: "#6b7280" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          <FilterTextField
            fullWidth
            multiline
            rows={3}
            label="Reason"
            name="reason"
            value={values.reason || ""}
            onChange={handleInputChange}
            placeholder="Enter reason for rejection..."
          />

          <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
            <Button
              onClick={() => setOpenRejectModal(false)}
              variant="outlined"
              sx={{ textTransform: "none", borderRadius: "8px", color: "#374151", borderColor: "#d1d5db" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "rgb(239,68,68)",
                boxShadow: "none",
                "&:hover": { backgroundColor: "rgb(220,38,38)", boxShadow: "none" },
              }}
            >
              Reject
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Filter Drawer ── */}
      <Drawer anchor="right" open={filter} onClose={() => { setFilter(false); resetForm(); setFilterData(!filterData); }}>
        <Box
          sx={{ width: 320, height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fff" }}
          component="form"
          onSubmit={(e) => { e.preventDefault(); setFilterData(!filterData); setFilter(false); }}
        >
          <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#111827" }}>Filter</Typography>
            <IconButton size="small" onClick={() => setFilter(false)} sx={{ color: "#6b7280" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ px: 3, py: 2, flex: 1, overflowY: "auto" }}>
            <FilterLabel>User Name</FilterLabel>
            <FilterTextField fullWidth name="userName" value={values.userName} onChange={handleInputChange} />

            <FilterLabel>Company Name</FilterLabel>
            <FilterTextField fullWidth name="companyName" value={values.companyName} onChange={handleInputChange} />

            <FilterLabel>Email</FilterLabel>
            <FilterTextField fullWidth name="email" value={values.email} onChange={handleInputChange} />

            <FilterLabel>Phone Number</FilterLabel>
            <FilterTextField fullWidth name="mobile" value={values.mobile} onChange={handleInputChange} />
          </Box>

          <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #f3f4f6", display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { resetForm(); setFilterData(!filterData); setFilter(false); }}
              sx={{ textTransform: "none", borderRadius: "10px", color: "#374151", borderColor: "#d1d5db", fontWeight: 600 }}
            >
              Reset
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{ textTransform: "none", borderRadius: "10px", backgroundColor: "rgb(79,70,229)", boxShadow: "none", fontWeight: 600, "&:hover": { backgroundColor: "rgb(67,56,202)", boxShadow: "none" } }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Notification notify={notify} setNotify={setNotify} />
    </>
  );
}
